import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Fab,
  Tooltip,
  Badge,
  useMediaQuery,
  useTheme,
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  CircularProgress,
  Button,
  IconButton,
} from "@mui/material";
import {
  Support as SupportIcon,
  Close as CloseIcon,
  SmartToy as AIIcon,
  QuestionAnswer as ChatIcon,
  LiveHelp as HelpIcon,
  Person as PersonIcon,
  Analytics as AnalyticsIcon,
} from "@mui/icons-material";
import { keyframes } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { aiSupportApi } from "../../services/api/ai-support.api";
import { supportApi, SupportAnalytics } from "../../services/api/support.api";
import { useRole } from "../../contexts/RoleContext";
import { canSupportUsers } from "../../utils/role-helpers";
import { useAiConsent } from "../../hooks/useAiConsent";

import { formatDistanceToNow } from "date-fns";
import { getDateFnsLocale } from "../../utils/date-utils";

// Анимации для привлечения внимания
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

type PositionStyles = {
  position: "fixed";
  zIndex: number;
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
};

// Типы для AI поддержки
interface QuickAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  action: () => void;
  category: "ai" | "human" | "self-service" | "support";
}

interface SupportFloatingButtonProps {
  position?: "bottom-right" | "bottom-left" | "top-right" | "top-left";
  showBadge?: boolean;
  enableQuickActions?: boolean;
  proactiveHelp?: boolean;
  customActions?: QuickAction[];
  /**
   * Состояние поддержки приходит из общего инстанса useSupport в SupportWrapper.
   * Свой useSupport() здесь НЕ создаём — иначе isOpen лаунчера и виджета
   * расходятся (диалог не открывается). Единый источник состояния.
   */
  openSupport: (initialMessage?: string) => void;
  /**
   * Просит оператора: открывает окно и поднимает флаг, а сам вызов эскалации
   * делает `SupportWidget` — в той сессии и в той ленте, которую человек видит.
   * Прежде лаунчер эскалировал СВОЮ сессию хука, пустую и невидимую (F-SUP-2).
   */
  requestEscalation: () => void;
  isOpen: boolean;
  unreadCount: number;
  /**
   * Кнопку «Связаться с оператором» (L2) показываем ТОЛЬКО когда true —
   * т.е. после 3 неуспешных AI-попыток (ТЗ §1.1). Источник — общий useSupport.
   */
  canRequestOperator: boolean;
}

const SupportFloatingButton: React.FC<SupportFloatingButtonProps> = ({
  position = "bottom-right",
  showBadge = true,
  enableQuickActions = true,
  proactiveHelp = true,
  customActions = [],
  openSupport,
  requestEscalation,
  isOpen,
  unreadCount,
  canRequestOperator,
}) => {
  const { t } = useTranslation("support");
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const { currentRole } = useRole();

  // Проверяем, имеет ли пользователь доступ к Support API
  const hasSupportAccess = canSupportUsers(currentRole);

  // Согласие на внешнюю AI-обработку (ADR-008). fail-closed: пока не дано —
  // AI-эндпоинты недоступны (403). Источник — GET /gdpr/consent (кэш React Query).
  const { hasAiConsent } = useAiConsent();

  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, _setIsVisible] = useState(true);
  const [showProactiveHint, setShowProactiveHint] = useState(false);

  const { data: aiHealth, isLoading: aiLoading } = useQuery({
    queryKey: ["support", "ai-health"],
    queryFn: async () => {
      return aiSupportApi.getHealth();
    },
    // /ai-support/actions/ai-health за AiProcessingConsentGuard (ADR-008): без
    // согласия эндпоинт отдаёт 403. Компонент смонтирован глобально (SupportWrapper),
    // поэтому безусловный поллинг давал фоновый 403 «Network Error» на КАЖДОЙ странице.
    // Гейт: (1) hasAiConsent (fail-closed) устраняет 403; (2) isExpanded — статус AI
    // читается только в раскрытой панели, фоновый поллинг не нужен (нет потребителя).
    enabled: hasAiConsent && isExpanded,
    refetchInterval: 60_000,
    retry: false, // 403 — политика доступа, а не сетевой сбой; ретрай бессмыслен
  });

  const { data: supportAnalytics, isLoading: analyticsLoading } = useQuery<
    {
      stats: SupportAnalytics["stats"];
      performance: SupportAnalytics["performance"]["agentStats"];
    },
    Error
  >({
    queryKey: ["support", "analytics", "stats"],
    queryFn: async () => {
      const response = await supportApi.getAnalytics();
      return {
        stats: response.data.stats,
        performance: response.data.performance?.agentStats,
      };
    },
    staleTime: 60_000,
    enabled: hasSupportAccess, // Запрос только для пользователей с ролью Support
  });

  const { data: ticketsSummary, isLoading: _ticketsLoading } = useQuery({
    queryKey: ["support", "tickets", "summary"],
    queryFn: async () => {
      const response = await supportApi.getTickets({ page: 1, limit: 5 });
      return response.data;
    },
    enabled: hasSupportAccess, // Запрос только для пользователей с ролью Support
  });

  const quickActions = useMemo<QuickAction[]>(() => {
    const actions: QuickAction[] = [
      {
        id: "ai-chat",
        icon: <AIIcon fontSize="small" />,
        label: t("quickActions.aiChat"),
        category: "ai",
        action: () => openSupport(),
      },
      {
        // Отдельная линия техподдержки (тикет/обращение). Ведёт на существующую
        // страницу /support (SupportPage) — будущий дом структуры L1/L2/L3.
        // НЕ дублирует AI-чат: это переход на портал поддержки, а не диалог.
        // Категория 'support' (НЕ 'human') — чтобы чип не путали с L2-«Оператор».
        id: "tech-support",
        icon: <SupportIcon fontSize="small" />,
        label: t("quickActions.techSupport"),
        category: "support",
        action: () => navigate("/support"),
      },
      // «Связаться с оператором» (L2) — НЕ показываем сразу. Доступно только
      // после 3 неуспешных AI-попыток (ТЗ §1.1): пользователь сперва проходит
      // линию AI и его рекомендации. canRequestOperator приходит из useSupport.
      ...(canRequestOperator
        ? [
            {
              id: "human-support",
              icon: <PersonIcon fontSize="small" />,
              label: t("quickActions.humanSupport"),
              category: "human" as const,
              action: () => requestEscalation(),
            },
          ]
        : []),
      {
        id: "help-center",
        icon: <HelpIcon fontSize="small" />,
        label: t("quickActions.helpCenter"),
        category: "self-service",
        action: () => navigate("/support/knowledge-base"),
      },
      {
        id: "analytics",
        icon: <AnalyticsIcon fontSize="small" />,
        label: t("quickActions.analytics"),
        category: "self-service",
        action: () => navigate("/support/analytics"),
      },
      ...customActions,
    ];

    if (ticketsSummary?.tickets.length) {
      actions.unshift({
        id: "recent-ticket",
        icon: <ChatIcon fontSize="small" />,
        label: t("quickActions.recentTicket"),
        category: "human",
        action: () => {
          const first = ticketsSummary.tickets[0];
          if (first != null) navigate(`/support/tickets/${first.id}`);
        },
      });
    }

    return actions;
  }, [
    canRequestOperator,
    customActions,
    requestEscalation,
    navigate,
    openSupport,
    t,
    ticketsSummary?.tickets,
  ]);

  useEffect(() => {
    if (!proactiveHelp || isOpen) return;

    const timer = setTimeout(() => setShowProactiveHint(true), 30_000);
    return () => clearTimeout(timer);
  }, [isOpen, proactiveHelp]);

  const handleToggleExpanded = () => {
    setIsExpanded((prev) => !prev);
    setShowProactiveHint(false);
  };

  const getPositionStyles = (): PositionStyles => {
    const baseStyles: PositionStyles = { position: "fixed", zIndex: 2000 };
    const offset = isMobile ? 16 : 24;
    switch (position) {
      case "bottom-right":
        return { ...baseStyles, bottom: offset, right: offset };
      case "bottom-left":
        return { ...baseStyles, bottom: offset, left: offset };
      case "top-right":
        return { ...baseStyles, top: offset, right: offset };
      case "top-left":
        return { ...baseStyles, top: offset, left: offset };
      default:
        return { ...baseStyles, bottom: offset, right: offset };
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const positionStyles = useMemo(
    () => getPositionStyles(),
    [position, isMobile],
  );

  const getCategoryColor = useCallback(
    (category: QuickAction["category"]) => {
      switch (category) {
        case "ai":
          return theme.palette.primary.main;
        case "human":
          return theme.palette.success.main;
        case "self-service":
          return theme.palette.info.main;
        case "support":
          return theme.palette.warning.main;
        default:
          return theme.palette.grey[500];
      }
    },
    [theme],
  );

  if (!isVisible) return null;

  const chatbotService = aiHealth?.services?.["chatbot"] as
    | string
    | { status?: string }
    | undefined;
  const chatbotStatus =
    typeof chatbotService === "string"
      ? chatbotService
      : chatbotService?.status;

  const aiBadgeColor:
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning" =
    chatbotStatus && chatbotStatus !== "offline" ? "success" : "warning";
  // AD-333: без «времени работы» — BE его не считает; прежнее значение было выдуманным.
  const aiStatusLabel = aiHealth
    ? aiHealth.status.toUpperCase()
    : t("ai.checking");

  const latestTicket = ticketsSummary?.tickets?.[0];
  const latestTicketTime = latestTicket
    ? formatDistanceToNow(new Date(latestTicket.updatedAt), {
        addSuffix: true,
        locale: getDateFnsLocale(),
      })
    : null;

  return (
    <>
      {showProactiveHint && !isExpanded && (
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            bottom: isMobile ? 72 : 88,
            ...(positionStyles.right !== undefined
              ? { right: positionStyles.right }
              : {}),
            ...(positionStyles.left !== undefined
              ? { left: positionStyles.left }
              : {}),
            ...(positionStyles.top !== undefined
              ? { top: (positionStyles.top as number) + (isMobile ? 56 : 64) }
              : {}),
            p: 2,
            maxWidth: isTablet ? 240 : 320,
            zIndex: 1999,
            animation: "fadeInUp 0.25s ease-out",
            "@keyframes fadeInUp": {
              from: { opacity: 0, transform: "translateY(8px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1}
          >
            <Typography variant="subtitle2" gutterBottom>
              {t("proactive.title")}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setShowProactiveHint(false)}
              aria-label="dismiss"
              sx={{ mt: -0.5, mr: -0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {t("proactive.description")}
          </Typography>
          <Button
            size="small"
            sx={{ mt: 1 }}
            onClick={() => {
              setShowProactiveHint(false);
              openSupport();
            }}
          >
            {t("proactive.action")}
          </Button>
        </Paper>
      )}

      {/* No Zoom wrapper — MUI Zoom applies CSS transforms which create a new
          containing block for position:fixed children, putting the FAB off-screen. */}
      <Tooltip title={t("tooltip.openSupport")} placement="left" arrow>
        <Box sx={getPositionStyles()}>
          <Badge
            color="error"
            badgeContent={showBadge ? unreadCount : 0}
            overlap="circular"
          >
            <Fab
              color={isExpanded ? "secondary" : "primary"}
              size={isMobile ? "medium" : "large"}
              sx={{
                animation: showProactiveHint ? `${pulse} 2s infinite` : "none",
                ...(showProactiveHint
                  ? { boxShadow: "0 8px 24px rgba(25, 118, 210, 0.4)" }
                  : {}),
              }}
              onClick={handleToggleExpanded}
            >
              {isExpanded ? <CloseIcon /> : <SupportIcon />}
            </Fab>
          </Badge>
        </Box>
      </Tooltip>

      <Paper
        elevation={12}
        sx={{
          position: "fixed",
          bottom: isMobile ? 72 : 88,
          ...(positionStyles.right !== undefined
            ? { right: positionStyles.right }
            : {}),
          ...(positionStyles.left !== undefined
            ? { left: positionStyles.left }
            : {}),
          ...(positionStyles.top !== undefined
            ? { top: (positionStyles.top as number) + (isMobile ? 56 : 64) }
            : {}),
          zIndex: 2000,
          width: isMobile ? "calc(100% - 32px)" : 360,
          maxWidth: 420,
          maxHeight: "calc(100vh - 120px)",
          overflowY: "auto",
          p: 2,
          borderRadius: 3,
          opacity: isExpanded ? 1 : 0,
          transform: isExpanded
            ? "translateY(0) scale(1)"
            : "translateY(16px) scale(0.95)",
          transition: "opacity 0.25s ease, transform 0.25s ease",
          pointerEvents: isExpanded ? "auto" : "none",
          visibility: isExpanded ? "visible" : "hidden",
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6">{t("title")}</Typography>
            <Chip size="small" label={aiStatusLabel} color={aiBadgeColor} />
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle2" color="text.secondary">
              {t("stats.title")}
            </Typography>
            <Stack direction="row" spacing={2}>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {t("stats.unread")}
                </Typography>
                <Typography variant="h6">{unreadCount}</Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {t("stats.responseTime")}
                </Typography>
                <Typography variant="h6">
                  {analyticsLoading
                    ? "—"
                    : `${supportAnalytics?.stats?.averageResponseTime ?? 0} ${t("stats.minutesShort", { defaultValue: "min" })}`}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {t("stats.satisfaction")}
                </Typography>
                <Typography variant="h6">
                  {analyticsLoading
                    ? "—"
                    : `${Math.round((supportAnalytics?.stats?.satisfactionRate ?? 0) * 100)}%`}
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          {latestTicket && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: "action.hover",
              }}
            >
              <Typography variant="subtitle2">
                {t("latestTicket.title")}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {latestTicket.title}
              </Typography>
              <Stack direction="row" spacing={1} mt={1} alignItems="center">
                <Chip
                  size="small"
                  label={t(`tickets.priority.${latestTicket.priority}`)}
                />
                <Typography variant="caption" color="text.secondary">
                  {latestTicketTime}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} mt={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    navigate(`/support/tickets/${latestTicket.id}`)
                  }
                >
                  {t("latestTicket.open")}
                </Button>
                <Button
                  size="small"
                  onClick={() => navigate("/support/tickets")}
                >
                  {t("latestTicket.viewAll")}
                </Button>
              </Stack>
            </Box>
          )}

          {enableQuickActions && (
            <Stack spacing={1}>
              <Typography variant="subtitle2" color="text.secondary">
                {t("quickActions.title")}
              </Typography>
              <Stack spacing={1}>
                {quickActions.map((action) => (
                  <Button
                    key={action.id}
                    variant="outlined"
                    color="inherit"
                    size="small"
                    onClick={action.action}
                    startIcon={action.icon}
                    sx={{ justifyContent: "flex-start" }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        size="small"
                        label={t(`quickActions.category.${action.category}`)}
                        sx={{
                          bgcolor: getCategoryColor(action.category),
                          color: "common.white",
                        }}
                      />
                      <Typography variant="body2">{action.label}</Typography>
                    </Box>
                  </Button>
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>

        {aiLoading && (
          <Box display="flex" justifyContent="center" mt={2}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Paper>
    </>
  );
};

export default SupportFloatingButton;
